/* eslint-disable no-underscore-dangle */

import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import orderBy from 'lodash/orderBy';
import { Link } from 'react-router-dom';

import * as api from 'Shared/api';
import { getRecommendationAnalytics } from 'Shared/actions/organizations';
import { recommendationKlassDisplayName } from 'Shared/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Meter from './Meter';
import HorizontalBar from './HorizontalBar';
import ActivityFeed from './ActivityFeed';
import TimeSeriesChart from './components/TimeSeriesChart/TimeSeriesChart';
import DetailNudgesModal from './components/DetailNudgesModal/DetailNudgesModal';
import SalesforceUserTagsFilter from './SalesforceUserTagsFilter';
import { WpIcon } from 'Wpds/components/icon';

import './Analytics.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

const percentage = (numerator, denominator) => {
  if (denominator === 0) {
    return 0;
  }
  return Math.round((numerator / denominator) * 100);
};

const sortableTableHeaders = [
  { value: 'currently_open_nudges', display: 'Nudges Open' },
  // { value: 'opened_nudges', display: 'Nudges Opened' },
  { value: 'closed_nudges', display: 'Nudges Closed' },
  { value: 'percentage_closed_nudges', display: 'Percent Closed' },
  { value: 'dismissed_nudges', display: 'Nudges Dismissed' },
];

const Analytics = () => {
  const dispatch = useDispatch();

  const { app } = useApp();
  const analytics = useSelector(state => state.recommendationAnalytics.team);

  const [tagsFilter, setTagsFilter] = useState([]);
  const [detailNudges, setDetailNudges] = useState([]);
  const [detailNudgesDescription, setDetailNudgesDescription] = useState('');
  const [detailNudgesFilter, setDetailNudgesFilter] = useState(false);
  const { salesAssistantTags: salesAssistantTagsFlag } = useFlags();

  const handleTagsFilterChange = v => setTagsFilter(v);

  useEffect(() => {
    dispatch(getRecommendationAnalytics({ tags: tagsFilter?.map(({ value }) => value) }));
  }, [dispatch, tagsFilter]);

  useEffect(() => {
    if (detailNudgesFilter) {
      setDetailNudges([]);
      setDetailNudgesDescription('Loading nudges...');

      api.getFilteredRecommendations(detailNudgesFilter).then(response => {
        setDetailNudges(response.data.recommendations);
        setDetailNudgesDescription(response.data.description);
      });
    }

    window.analytics.track('Viewed NudgeBot Analytics', { app_id: app.id });
  }, [detailNudgesFilter]);

  const { organization } = useOrganization();
  const salesforceUserTags = organization.salesforce_user_tags;

  const [sortBy, setSortBy] = useState('currently_open_nudges');
  const [sortDirection, setSortDirection] = useState('desc');
  const sortedTableData = useCallback(() => {
    if (!analytics) {
      return [];
    }
    if (sortBy === 'name') {
      return orderBy(analytics.table, row => row.user.name, [sortDirection]);
    }
    if (sortBy === 'role') {
      return orderBy(analytics.table, row => row.user.role, [sortDirection]);
    }
    if (sortBy === 'percentage_closed_nudges') {
      return orderBy(analytics.table, row => percentage(row.closed_nudges, row.opened_nudges), [sortDirection]);
    }

    return orderBy(analytics.table, [sortBy], [sortDirection]);
  }, [sortBy, sortDirection, analytics]);

  const setSort = value => {
    let direction = 'desc';

    if (value === sortBy && sortDirection === 'desc') {
      direction = 'asc';
    }

    setSortBy(value);
    setSortDirection(direction);
  };

  const sortIcon = value => {
    if (sortBy === value && sortDirection === 'asc') {
      return <WpIcon name="sort-up" className="ml-2" />;
    }
    if (sortBy === value && sortDirection === 'desc') {
      return <WpIcon name="sort-down" className="ml-2" />;
    }

    return <WpIcon name="sort" className="ml-2" style={{ color: '#ddd' }} />;
  };

  if (!analytics) {
    return <div className="p4">Loading leaderboard...</div>;
  }

  const percentageClosed = percentage(analytics.totals.closed_nudges, analytics.totals.opened_nudges);

  return (
    <div className="nudgebot-analytics">
      <div className="d-flex">
        <div className="main-content">
          <div className="d-flex flex-wrap">
            <div className="col-sm-12 col-lg-6 col-xl-6">
              <h3 className="mt-4 ml-3">CRM Quality Leaderboard</h3>
            </div>
            <div className="col-sm-12 col-lg-6 col-xl-6 mt-4">
              {salesAssistantTagsFlag && (
                <SalesforceUserTagsFilter
                  tags={tagsFilter}
                  tagOptions={salesforceUserTags}
                  handleChange={handleTagsFilterChange}
                />
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap">
            <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
              <div className="mb-2 mt-4 p-3 border rounded  nudgebot-card">
                <h5 className="mt-0 mb-4">Team CRM Data Quality Score</h5>
                <Meter totalPercentage={percentageClosed} />
                <p className="small text-muted mt-3">
                  {`${percentageClosed}% `}
                  {`(${analytics.totals.closed_nudges}/${analytics.totals.opened_nudges}) `}
                  of opened nudges have been closed.
                </p>
              </div>
            </div>

            <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
              <div className="mb-2 mt-4 p-3 border rounded  nudgebot-card">
                <h5 className="mt-0 mb-4">Types of Open Nudges</h5>
                <div className="nudgebot-chart">
                  {analytics.hygiene_score_by_recommendation_setting_name
                    .sort((a, b) => b.opened - a.opened)
                    .map(({ recommendation_setting_name, currently_open_nudges }) => (
                      <HorizontalBar
                        key={recommendation_setting_name}
                        title={recommendationKlassDisplayName(recommendation_setting_name)}
                        onClickBar={() =>
                          setDetailNudgesFilter({
                            recommendation_setting_name,
                            recommendation_status: 'open',
                            tags: tagsFilter?.map(({ value }) => value),
                          })
                        }
                        totalValue={analytics.totals.currently_open_nudges}
                        value={currently_open_nudges}
                      />
                    ))}
                </div>
              </div>
            </div>

            <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
              <div className="mb-2 p-3 border rounded nudgebot-card">
                <TimeSeriesChart
                  title="CRM Data Quality Over Time"
                  chartKey="hygiene_timeseries"
                  setDetailNudgesFilter={setDetailNudgesFilter}
                  tags={tagsFilter}
                />
              </div>
            </div>

            <div className="col-sm-12 col-lg-6 col-xl-6 mb-4">
              <div className="mb-2 p-3 border rounded  nudgebot-card">
                <TimeSeriesChart
                  title="Team Nudges Over Time"
                  chartKey="events_timeseries"
                  setDetailNudgesFilter={setDetailNudgesFilter}
                  tags={tagsFilter}
                />
              </div>
            </div>

            <div className="col-sm-12 col-lg-12 col-xl-12">
              <div className="table-container border rounded px-4 pt-4 pb-0 mb-4">
                <h5 className="mt-0 mb-4">Individual Metrics</h5>
                <table className="table">
                  <thead>
                    <tr>
                      <th scope="col" onClick={() => setSort('name')}>
                        Name
                        {sortIcon('name')}
                      </th>
                      <th scope="col" onClick={() => setSort('role')}>
                        Role
                        {sortIcon('role')}
                      </th>
                      {sortableTableHeaders.map(({ value, display }) => (
                        <th key={value} onClick={() => setSort(value)} scope="col" className="text-right">
                          {display}
                          {sortIcon(value)}
                        </th>
                      ))}
                      <th scope="col" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTableData().map(row => (
                      <tr key={row.user.name}>
                        <td>
                          <Link to={`/recommendation_analytics/${row.user.id}`}>{row.user.name}</Link>
                        </td>
                        <td>{row.user.role}</td>
                        <td className="text-right">{row.currently_open_nudges}</td>
                        {/* <td className="text-right">{row.opened_nudges}</td> */}
                        <td className="text-right">{row.closed_nudges}</td>
                        <td className="text-right">{`${percentage(row.closed_nudges, row.opened_nudges)}%`}</td>
                        <td className="text-right">{row.dismissed_nudges}</td>
                        <td className="text-right">
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() =>
                              setDetailNudgesFilter({ user_id: row.user.id, recommendation_status: 'open' })
                            }
                          >
                            View open nudges
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar" style={{ flexShrink: 0 }}>
          <ActivityFeed feed={Object.entries(analytics.activity_feed)} />
        </div>
      </div>

      {detailNudgesFilter && (
        <DetailNudgesModal
          detailNudgesFilter={detailNudgesFilter}
          detailNudgesDescription={detailNudgesDescription}
          setDetailNudgesFilter={setDetailNudgesFilter}
          detailNudges={detailNudges}
        />
      )}
    </div>
  );
};

Analytics.propTypes = {};

export default Analytics;
